import axios from 'axios';
import get from 'lodash/get';
import isString from 'lodash/isString';
import isEmpty from 'lodash/isEmpty';
import defaultsDeep from 'lodash/defaultsDeep';
import { compare } from 'fast-json-patch';
import merge from 'lodash/merge';
import getRequestHeaders from './headers';

const setResponseInterceptor = (
  {
    accountHost,
  }: {
    accountHost: string;
  } = { accountHost: '' },
) => {
  axios.interceptors.response.use(
    res => res,
    error => {
      const statusCode = get(error, 'response.status', 500);
      if (statusCode === 401) {
        // redirect to login
        window.onbeforeunload = null;
        // check if we are in an iframe
        if (window.self !== window.top) {
          return window.location.replace(
            `${accountHost}/embed/login?redirect_url=${encodeURIComponent(
              get(window, 'location.pathname', ''),
            )}`,
          );
        }
        return window.location.replace(
          `${accountHost}/login?redirect_url=${encodeURIComponent(
            get(window, 'location.pathname', ''),
          )}`,
        );
      }
      return Promise.reject(error);
    },
  );
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const getConfig = (options?: Record<any, any> | null) =>
  defaultsDeep(options, {
    credentials: 'same-origin',
    headers: {
      ...getRequestHeaders(),
    },
  });

const patchRequest = async (
  url: string,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  object: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  newObject: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  options?: Record<any, any> | null,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
): Promise<any> => {
  const patchArray = compare(object, newObject).map(patchObj => {
    const value = get(patchObj, 'value', undefined);
    return {
      ...patchObj,
      // If value is specified, BFF/backend expect it to be a string
      // Undefined is filtered out by axios
      value: isString(value) ? value : JSON.stringify(value),
    };
  });
  if (isEmpty(patchArray)) {
    // if empty, does a get
    return axios.get(url, getConfig(options));
  }
  return axios.patch(url, patchArray, getConfig(options));
};

const patchAllRequest = async (
  url: string,
  requests: {
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    context: any;
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    newObject: any;
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    oldObject: any;
  }[],
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  options?: Record<any, any> | null,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
): Promise<any> => {
  const patchObjects = requests.map(request => {
    const patch = compare(request.oldObject, request.newObject).map(
      patchObj => {
        const value = get(patchObj, 'value', undefined);
        return {
          ...patchObj,
          // If value is specified, BFF/backend expect it to be a string
          // Undefined is filtered out by axios
          value: isString(value) ? value : JSON.stringify(value),
        };
      },
    );
    return { [request.newObject.id]: { patch, context: request.context } };
  });
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2556
  const patchObject = merge(...patchObjects);
  return axios.patch(url, patchObject, getConfig(options));
};

const putRequest = async (
  url: string,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  putObject: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  options?: Record<any, any> | null,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
): Promise<any> => axios.put(url, putObject, getConfig(options));

const getRequest = async (
  url: string,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  options?: Record<any, any> | null,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
): Promise<any> => axios.get(url, getConfig(options));

const deleteRequest = async (
  url: string,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  options?: Record<any, any> | null,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
): Promise<any> => axios.delete(url, getConfig(options));

const postRequest = async (
  url: string,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  body: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  options?: Record<any, any> | null,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
): Promise<any> => axios.post(url, body, getConfig(options));

export {
  patchRequest,
  patchAllRequest,
  putRequest,
  postRequest,
  getRequest,
  deleteRequest,
  setResponseInterceptor,
};
